import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Loader2 } from "lucide-react";
import { HERO_IMAGE } from "@/data/content";
import { BrandWord, GoogleIcon } from "@/components/BrandWord";
import { toast } from "sonner";

export default function Register() {
  const { register, user, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard");
  }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — welcome to Fragua Global!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-navy">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10" data-testid="register-logo">
            <span className="w-9 h-9 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center">
              <Plane className="w-4 h-4 text-brand-gold" />
            </span>
            <BrandWord className="text-lg" />
          </Link>

          <h1 className="font-display text-3xl text-brand-text mb-2">Create account</h1>
          <p className="text-white/50 mb-8 text-sm">Begin your journey with us.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white/70">Full Name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input"
                className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input"
                className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-white/70">Phone / WhatsApp</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="register-phone-input"
                className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="0208636267" />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input id="password" type="password" required value={form.password} onChange={set("password")} data-testid="register-password-input"
                className="mt-2 bg-brand-surface border-white/10 text-white h-11" placeholder="Min. 6 characters" />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full h-11 rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-brand-navy px-3 text-white/40">or</span></div>
          </div>

          <Button type="button" onClick={googleLogin} data-testid="google-register-btn" variant="outline"
            className="w-full h-11 rounded-full border-white/15 bg-white text-brand-navy hover:bg-white/90 font-semibold">
            <GoogleIcon /> Sign up with Google
          </Button>

          <p className="text-white/50 text-sm mt-6 text-center">
            Already have an account? <Link to="/login" className="text-brand-gold hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO_IMAGE} alt="Travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-right">
          <h2 className="font-display text-4xl text-brand-text leading-tight">Join thousands exploring the world with Fragua.</h2>
        </div>
      </div>
    </div>
  );
}
